import React from "react";
import { Box, Typography, Chip, Stack, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/uk";
import { useTranslation } from "react-i18next";
import TripCard from "./TripCard";
import TransferItineraryCard from "./TransferItineraryCard";
import PartnerTripsList from "./PartnerTripsList";
import SkeletonTripList from "./SkeletonTripList";

const TripList = ({
  directTrips = [],
  transferItineraries = [],
  partnerTrips = [],
  loadingPartners = false,
  fromCity,
  toCity,
  searchDate,
  adults,
  children,
  onSelectTrip,
  hideTransfers = false,
  hidePartners = false,
}) => {
  const { t } = useTranslation("common");

  const transfers = hideTransfers ? [] : transferItineraries;
  const partners = hidePartners ? [] : partnerTrips;

  const hasDirect = directTrips.length > 0;
  const hasTransfers = transfers.length > 0;
  const hasPartners = partners.length > 0;

  if (!hasDirect && !hasTransfers && !hasPartners && !loadingPartners) {
    return (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ textAlign: "center", py: 3 }}
      >
        {t("trips.noTripsFound")}
      </Typography>
    );
  }

  const formattedSearchDate = searchDate.format("DD.MM.YYYY");

  const tripsOnSearchDate = directTrips.filter(
    (trip) => dayjs(trip.departure_date).format("DD.MM.YYYY") === formattedSearchDate
  );

  const nearestTrips = directTrips
    .filter(
      (trip) => dayjs(trip.departure_date).format("DD.MM.YYYY") !== formattedSearchDate
    )
    .sort((a, b) => dayjs(a.departure_date).valueOf() - dayjs(b.departure_date).valueOf());

  const getLeg1Date = (itinerary) => {
    const leg1 = itinerary.legs?.find((l) => l.leg_index === 1) ?? itinerary.legs?.[0];
    return leg1?.trip?.departure_date;
  };

  const transfersOnSearchDate = transfers.filter((itinerary) => {
    const dep = getLeg1Date(itinerary);
    return dep && dayjs(dep).format("DD.MM.YYYY") === formattedSearchDate;
  });

  const nearestTransfers = transfers
    .filter((itinerary) => {
      const dep = getLeg1Date(itinerary);
      return dep && dayjs(dep).format("DD.MM.YYYY") !== formattedSearchDate;
    })
    .sort((a, b) => dayjs(getLeg1Date(a)).valueOf() - dayjs(getLeg1Date(b)).valueOf());

  const SectionHeader = ({ title, count }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 2,
        flexWrap: "wrap",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
      <Chip
        size="small"
        label={count}
        sx={{
          fontWeight: 600,
          bgcolor: "primary.main",
          color: "primary.contrastText",
        }}
      />
    </Box>
  );

  const transferKey = (itinerary) =>
    itinerary.legs?.map((l) => l.trip?.id).join("-") ?? Math.random();

  return (
    <Box sx={{ width: "100%", maxWidth: 900, mx: "auto" }}>
      {hasDirect && (
        <>
          {tripsOnSearchDate.length > 0 ? (
            <Box sx={{ mb: 4 }}>
              <SectionHeader
                title={t("trips.tripsOnDate", { date: searchDate.format("DD.MM.YYYY") })}
                count={tripsOnSearchDate.length}
              />
              <Stack spacing={2} component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {tripsOnSearchDate.map((trip) => (
                  <Box component="li" key={trip.id}>
                    <TripCard
                      trip={trip}
                      fromCity={fromCity}
                      toCity={toCity}
                      adults={adults}
                      children={children}
                      onSelect={onSelectTrip}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                px: 2,
                borderRadius: 2,
                bgcolor: "action.hover",
                mb: 4,
              }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                {t("trips.noTripsOnDate", { date: searchDate.format("DD.MM.YYYY") })}
              </Typography>
            </Box>
          )}

          {nearestTrips.length > 0 && (
            <Box sx={{ mb: hasTransfers ? 4 : 0 }}>
              <SectionHeader title={t("trips.nearestTrips")} count={nearestTrips.length} />
              <Stack spacing={2} component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {nearestTrips.map((trip) => (
                  <Box component="li" key={trip.id}>
                    <TripCard
                      trip={trip}
                      fromCity={fromCity}
                      toCity={toCity}
                      adults={adults}
                      children={children}
                      onSelect={onSelectTrip}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </>
      )}

      {hasTransfers && (
        <Box>
          <SectionHeader
            title={t("search.transfersSection")}
            count={transfers.length}
          />
          {transfersOnSearchDate.length > 0 && (
            <Stack spacing={2} component="ul" sx={{ listStyle: "none", m: 0, p: 0, mb: 3 }}>
              {transfersOnSearchDate.map((itinerary) => (
                <Box component="li" key={transferKey(itinerary)}>
                  <TransferItineraryCard
                    itinerary={itinerary}
                    fromCity={fromCity}
                    toCity={toCity}
                    adults={adults}
                    children={children}
                    onSelect={onSelectTrip}
                  />
                </Box>
              ))}
            </Stack>
          )}
          {nearestTransfers.length > 0 && (
            <>
              {transfersOnSearchDate.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t("trips.noTripsOnDate", { date: searchDate.format("DD.MM.YYYY") })}
                </Typography>
              )}
              <Stack spacing={2} component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {nearestTransfers.map((itinerary) => (
                  <Box component="li" key={transferKey(itinerary)}>
                    <TransferItineraryCard
                      itinerary={itinerary}
                      fromCity={fromCity}
                      toCity={toCity}
                      adults={adults}
                      children={children}
                      onSelect={onSelectTrip}
                    />
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>
      )}

      {(hasPartners || (loadingPartners && !hidePartners)) && (
        <Box sx={{ mt: hasDirect || hasTransfers ? 4 : 0 }}>
          <SectionHeader
            title={t("trips.partnerSection", { defaultValue: "Інші перевізники" })}
            count={loadingPartners ? "…" : partners.length}
          />
          {loadingPartners && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: hasPartners ? 2 : 0,
                py: 1,
              }}
            >
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                {t("trips.searchingPartners", {
                  defaultValue: "Шукаємо рейси інших перевізників…",
                })}
              </Typography>
            </Box>
          )}
          {loadingPartners && !hasPartners && (
            <SkeletonTripList count={2} width="100%" />
          )}
          {hasPartners && (
            <PartnerTripsList
              partnerTrips={partners}
              searchDate={searchDate}
              fromCity={fromCity}
              toCity={toCity}
              adults={adults}
              children={children}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default TripList;
